import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import ProgressBar from '../ui/ProgressBar'
import Switch from '../ui/Switch'

const {
  FiEye,
  FiUser,
  FiStar,
  FiGift,
  FiSettings,
  FiLock,
  FiUnlock,
  FiClock,
  FiTrendingUp,
  FiTarget
} = FiIcons

const ChildViewSettings = () => {
  const { family } = useAuth()
  const { getChildPoints, getChildLevel, tasks, completions } = useFamily()
  const [selectedChild, setSelectedChild] = useState(family?.children?.[0]?.id || null)
  const [childSettings, setChildSettings] = useState({
    showPoints: true,
    showLevel: true,
    showProgress: true,
    showRewards: true,
    showCompletedTasks: true,
    showLeaderboard: false,
    allowTaskSelection: true,
    requireParentApproval: false,
    showAnalytics: false,
    timeRestrictions: false,
    startTime: '06:00',
    endTime: '21:00'
  })

  // If family or children aren't loaded yet, show loading
  if (!family) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading family data...</p>
        </div>
      </Card>
    )
  }

  const child = family?.children?.find(c => c.id === selectedChild)
  const childPoints = child ? getChildPoints(child.id) : 0
  const childLevel = getChildLevel(childPoints)

  // Get child-specific stats
  const getChildStats = (childId) => {
    if (!childId) return {
      totalCompletions: 0,
      todayCompletions: 0,
      totalPoints: 0,
      availableTasks: 0
    }

    const childCompletions = completions.filter(c => c.child_id === childId)
    const todayCompletions = childCompletions.filter(c => {
      const today = new Date().toDateString()
      return new Date(c.completed_at).toDateString() === today
    })
    const availableTasks = tasks.filter(task => 
      !task.assigned_to || task.assigned_to === childId
    )

    return {
      totalCompletions: childCompletions.length,
      todayCompletions: todayCompletions.length,
      totalPoints: childCompletions.reduce((sum, c) => sum + (c.points_earned || 0), 0),
      availableTasks: availableTasks.length
    }
  }

  const handleSettingChange = (setting, value) => {
    setChildSettings(prev => ({
      ...prev,
      [setting]: value
    }))
  }

  const handleSaveSettings = () => {
    // Here you would save the settings to the database
    console.log('Saving child settings:', childSettings)
    // Could update the child record with custom settings
    alert(`Settings saved for ${child?.name || 'selected child'}! 📱`)
  }

  const previewChildView = () => {
    // This would open a modal or navigate to a preview of how the child sees their interface
    console.log('Opening child view preview for:', child?.name)
    alert(`Opening preview for ${child?.name || 'selected child'}! 👀`)
  }

  if (!family?.children || family.children.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No Children Added</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Add children to your family to customize their individual views and settings.
          </p>
          <Button variant="primary">
            Add Your First Child
          </Button>
        </div>
      </Card>
    )
  }

  const childStats = child ? getChildStats(child.id) : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <SafeIcon icon={FiEye} className="text-primary-500" />
              Child View Settings
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Customize what each child sees and can do in their interface
            </p>
          </div>
          <Button variant="outline" onClick={previewChildView}>
            <SafeIcon icon={FiEye} className="text-sm" />
            Preview Child View
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Child Selector */}
        <div className="lg:col-span-1">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Select Child</h3>
            <div className="space-y-3">
              {family.children.map((familyChild) => {
                const stats = getChildStats(familyChild.id)
                const points = getChildPoints(familyChild.id)
                const level = getChildLevel(points)
                
                return (
                  <button
                    key={familyChild.id}
                    onClick={() => setSelectedChild(familyChild.id)}
                    className={`
                      w-full p-4 rounded-lg border-2 transition-all duration-200 text-left
                      ${selectedChild === familyChild.id 
                        ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">{familyChild.avatar || '👦'}</div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{familyChild.name}</div>
                        {familyChild.age && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">Age {familyChild.age}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Level {level.level} • {stats.totalPoints} points
                    </div>
                    <ProgressBar 
                      value={points} 
                      max={level.next} 
                      showLabel={false} 
                      color="primary" 
                      size="sm" 
                      className="mt-2" 
                    />
                  </button>
                )
              })}
            </div>
          </Card>

          {/* Quick Stats */}
          {child && childStats && (
            <Card className="mt-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {child.name}'s Stats
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Points</span>
                  <Badge variant="primary">{childStats.totalPoints}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tasks Completed</span>
                  <Badge variant="success">{childStats.totalCompletions}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Today</span>
                  <Badge variant="secondary">{childStats.todayCompletions}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Available Tasks</span>
                  <Badge variant="info">{childStats.availableTasks}</Badge>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Settings Panel */}
        <div className="lg:col-span-2">
          {child ? (
            <div className="space-y-6">
              {/* Visibility Settings */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Interface Visibility
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Control what {child.name} can see in their dashboard
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiStar} className="text-yellow-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Show Points</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Display earned points</div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.showPoints}
                      onChange={(checked) => handleSettingChange('showPoints', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiTrendingUp} className="text-blue-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Show Level</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Display current level</div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.showLevel}
                      onChange={(checked) => handleSettingChange('showLevel', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiTarget} className="text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Show Progress</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Display progress bars</div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.showProgress}
                      onChange={(checked) => handleSettingChange('showProgress', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiGift} className="text-purple-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Show Rewards</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Display available rewards</div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.showRewards}
                      onChange={(checked) => handleSettingChange('showRewards', checked)}
                    />
                  </div>
                </div>
              </Card>

              {/* Permissions Settings */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Permissions & Controls
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Set what {child.name} can do independently
                </p>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiUnlock} className="text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Allow Task Selection</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Let {child.name} choose which tasks to complete
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.allowTaskSelection}
                      onChange={(checked) => handleSettingChange('allowTaskSelection', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiLock} className="text-orange-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Require Parent Approval</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Tasks need parent confirmation before completion
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.requireParentApproval}
                      onChange={(checked) => handleSettingChange('requireParentApproval', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiUser} className="text-blue-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Show Leaderboard</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Display comparison with siblings
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.showLeaderboard}
                      onChange={(checked) => handleSettingChange('showLeaderboard', checked)}
                    />
                  </div>
                </div>
              </Card>

              {/* Time Restrictions */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Time Restrictions
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Set when {child.name} can access their chore dashboard
                </p>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiClock} className="text-purple-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Enable Time Restrictions</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Limit access to specific hours
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={childSettings.timeRestrictions}
                      onChange={(checked) => handleSettingChange('timeRestrictions', checked)}
                    />
                  </div>

                  {childSettings.timeRestrictions && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="grid grid-cols-2 gap-4 pl-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={childSettings.startTime}
                          onChange={(e) => handleSettingChange('startTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={childSettings.endTime}
                          onChange={(e) => handleSettingChange('endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </motion.div>
                  )}
                </div>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button variant="primary" onClick={handleSaveSettings}>
                  <SafeIcon icon={FiSettings} className="text-sm" />
                  Save Settings for {child.name}
                </Button>
              </div>
            </div>
          ) : (
            <Card>
              <div className="text-center py-12">
                <div className="text-4xl mb-4">👈</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Select a Child
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Choose a child from the sidebar to customize their settings
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChildViewSettings